import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Upload, KeyRound } from 'lucide-react';
import { playAdminStatus, uploadPlayKey, setPlayMode, uploadKeystore, fill, playBindings, resetPlayBinding } from '../lib/license';

const readFile = (f, asData) => new Promise((ok, bad) => {
  const r = new FileReader();
  r.onload = () => ok(r.result); r.onerror = bad;
  if (asData) r.readAsDataURL(f); else r.readAsText(f);
});

// subscription device bindings: list + reset (unbind)
function PlayBindings({ t }) {
  const [list, setList] = useState(null);
  const load = () => playBindings().then((r) => setList(r.bindings)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const reset = async (id) => { try { await resetPlayBinding(id); toast.success(t.pbResetOk); load(); } catch { toast.error(t.licOffline); } };
  if (!list) return null;
  return (
    <div className="pb-list" data-testid="play-bindings">
      <b>{t.pbBindTitle} ({list.length})</b>
      {!list.length && <small className="muted">{t.pbNone}</small>}
      {list.map((b) => (
        <div key={b.id} className="row pb-row" data-testid={`play-binding-${b.id}`}>
          <small>{t.pbDev}: <b>{b.device_id}</b> · {t.pbSwitches}: {b.switches} · {b.updated_at?.slice(0, 10)}</small>
          <button className="btn btn-outline btn-sm" data-testid={`play-binding-reset-${b.id}`} onClick={() => reset(b.id)}>{t.pbReset}</button>
        </div>
      ))}
    </div>
  );
}

export default function PlayAdmin({ t }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zip, setZip] = useState(null);
  const [pwd, setPwd] = useState('');
  const err = (e) => toast.error(t['playErr_' + e.message] || (e.message === 'owner' ? t.ownerExpired : t.licOffline));
  const run = async (fn) => { setBusy(true); try { const r = await fn(); setSt(r); return r; } catch (e) { err(e); return null; } finally { setBusy(false); } };
  useEffect(() => { playAdminStatus().then(setSt).catch(() => {}); }, []);

  const onKey = async (e) => {
    const f = e.target.files[0]; e.target.value = '';
    if (!f) return;
    const r = await run(async () => uploadPlayKey(await readFile(f)));
    if (!r) return;
    toast.success(t.playAdmKeyOk);
    if (r.missing?.length) toast.warning(fill(t.playAdmMissing, { list: r.missing.join(', ') }), { duration: 10000 });
  };
  const saveZip = async () => {
    if (await run(async () => uploadKeystore(await readFile(zip, true), pwd))) { toast.success(t.playAdmZipOk); setZip(null); setPwd(''); }
  };
  const live = st?.mode === 'live';

  return (
    <div className="ent-gen" data-testid="play-admin">
      <b><ShieldCheck size={14} /> {t.playAdmTitle}</b>
      <small data-testid="play-admin-status">
        {t.playAdmMode}: <b>{live ? t.playAdmLive : t.playAdmTest}</b> · {st?.client_email || t.playAdmNone}
        {st?.keystore ? ` · ${t.playAdmZipSaved}` : ''}
      </small>
      <div className="row">
        <label className="btn btn-proj" data-testid="play-key-upload-btn" aria-disabled={busy}>
          <Upload size={15} /> {t.playAdmKey}
          <input type="file" accept=".json,.txt,application/json,text/plain" hidden data-testid="play-key-input" onChange={onKey} disabled={busy} />
        </label>
        {st?.configured && (
          <button className="btn btn-outline btn-sm" data-testid="play-mode-toggle-btn" disabled={busy} onClick={() => run(() => setPlayMode(live ? 'test' : 'live'))}>
            {live ? t.playAdmToTest : t.playAdmToLive}
          </button>
        )}
      </div>
      <div className="row">
        <label className="btn btn-outline btn-sm" data-testid="play-zip-upload-btn">
          <KeyRound size={14} /> {zip ? zip.name : t.playAdmZip}
          <input type="file" accept=".zip,application/zip" hidden data-testid="play-zip-input" onChange={(e) => setZip(e.target.files[0] || null)} />
        </label>
        <input type="password" data-testid="play-zip-password-input" placeholder={t.playAdmPwd} value={pwd} onChange={(e) => setPwd(e.target.value)} />
        <button className="btn btn-dark btn-sm" data-testid="play-zip-save-btn" disabled={busy || !zip || !pwd} onClick={saveZip}><Upload size={14} /></button>
      </div>
      <PlayBindings t={t} />
    </div>
  );
}
